import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import {
  ArrowRight,
  Brain,
  Sparkles,
  Image,
  Video,
  Mic,
  FileText,
  Wand2,
  Check,
  Zap,
  Users,
  Camera,
  Share2,
  Palette
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const AIStudioPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleExplore = () => {
    if (user) {
      // Logged in - go directly to AI Social page
      navigate("/merchant/ai-social");
    } else {
      // Not logged in - go to login
      navigate("/login?redirect=/merchant/ai-social");
    }
  };

  const features = [
    {
      icon: FileText,
      title: "AI Script Writer",
      description: "Generate viral scripts for TikTok, Instagram Reels, and YouTube Shorts with GPT-5.2 powered creativity.",
      color: "indigo"
    },
    {
      icon: Image,
      title: "AI Image Generation",
      description: "Create stunning promotional images, product shots, and social media graphics with Nano Banana AI.",
      color: "violet"
    },
    {
      icon: Video,
      title: "AI Video Creation",
      description: "Transform your scripts into engaging videos with Sora 2 AI video generation.",
      color: "fuchsia"
    },
    {
      icon: Mic,
      title: "AI Voice Over",
      description: "Professional voice narration in multiple styles and accents using OpenAI TTS technology.",
      color: "pink"
    },
    {
      icon: Camera,
      title: "AI Face Swap",
      description: "Put your brand ambassador in any video with advanced face-swap technology.",
      color: "rose"
    },
    {
      icon: Share2,
      title: "Multi-Platform Posting",
      description: "Schedule and publish directly to TikTok, Instagram, YouTube, Facebook, and more.",
      color: "orange"
    }
  ];

  const creditPackages = [
    { credits: 500, price: 49, perCredit: "£0.098" },
    { credits: 2000, price: 149, perCredit: "£0.075", popular: true },
    { credits: 5000, price: 299, perCredit: "£0.060" }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-10 w-auto brightness-0 invert" />
              <div className="h-8 w-px bg-white/40" />
              <span className="text-white font-brand text-xl font-bold">VEYRONIX</span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Button
                  onClick={handleExplore}
                  className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:from-indigo-700 hover:via-violet-700 hover:to-fuchsia-600 text-white rounded-full px-6"
                  data-testid="explore-studio-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Open AI Studio
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-300 hover:text-white">
                      Sign In
                    </Button>
                  </Link>
                  <Button
                    onClick={handleExplore}
                    className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white rounded-full px-6"
                    data-testid="login-explore-btn"
                  >
                    Login to Explore
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-violet-900/20 to-fuchsia-900/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fuchsia-600/30 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500/20 to-fuchsia-500/20 border border-indigo-500/30 mb-6">
              <Brain className="h-5 w-5 text-indigo-400" />
              <span className="text-sm text-indigo-300">Powered by GPT-5.2, Sora 2 & Nano Banana</span>
            </div>
            
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              <span className="font-brand bg-gradient-to-r from-indigo-400 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                VEYRONIX AI STUDIO
              </span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Create viral social media content with the power of AI. Generate scripts, images, videos, and voice-overs in minutes, not hours.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={handleExplore}
                size="lg"
                className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:from-indigo-700 hover:via-violet-700 hover:to-fuchsia-600 text-white rounded-full px-8 h-14 text-lg"
                data-testid="hero-cta-btn"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                {user ? "Open AI Studio" : "Login to Explore"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-sm text-slate-400">
                <span className="text-emerald-400 font-semibold">FREE</span> for all logged-in users
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Everything You Need to <span className="bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">Go Viral</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              A complete AI-powered content creation suite for modern businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-slate-800/50 border border-white/5 hover:border-indigo-500/30 transition-all duration-300 group"
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${feature.color}-500/20 to-${feature.color}-600/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`h-6 w-6 text-${feature.color}-400`} />
                </div>
                <h3 className="font-semibold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Credits Section */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold mb-4">
              <Zap className="h-8 w-8 inline-block text-amber-400 mr-2" />
              AI Credits
            </h2>
            <p className="text-slate-400 text-lg">
              Power your AI creations with flexible credit packages
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {creditPackages.map((pkg, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all ${
                  pkg.popular
                    ? "bg-gradient-to-br from-amber-900/30 to-slate-900 border-amber-500/30 scale-105"
                    : "bg-slate-800/30 border-white/10"
                }`}
              >
                {pkg.popular && (
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400">
                      Best Value
                    </span>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-4xl font-bold text-white mb-1">{pkg.credits.toLocaleString()}</div>
                  <div className="text-slate-400 text-sm mb-4">credits</div>
                  <div className="text-3xl font-bold text-white mb-1">£{pkg.price}</div>
                  <div className="text-slate-500 text-xs">{pkg.perCredit} per credit</div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-400 text-sm">
              Credits never expire • Use across all AI features • Included with your merchant account
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-indigo-900/50 via-violet-900/50 to-fuchsia-900/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
            Ready to Create Amazing Content?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Join thousands of businesses using AI to dominate social media.
          </p>
          <Button
            onClick={handleExplore}
            size="lg"
            className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 h-14 text-lg font-semibold"
            data-testid="final-cta-btn"
          >
            {user ? "Open AI Studio" : "Login to Explore"}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={COMPANY_LOGO} alt="V" className="h-8 brightness-0 invert opacity-50" />
              <div className="h-6 w-px bg-white/20" />
              <span className="text-slate-400 font-brand text-sm font-bold">VEYRONIX</span>
            </Link>
            <p className="text-slate-500 text-sm">
              © 2025 <span className="font-brand">VEYRONIX</span>. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-400 hover:text-white text-sm">Home</Link>
              <Link to="/pricing" className="text-slate-400 hover:text-white text-sm">Pricing</Link>
              <a href="mailto:support@veyronix.co.uk" className="text-slate-400 hover:text-white text-sm">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AIStudioPage;
